import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Spinner } from './spinner';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-3 rounded-field font-semibold',
    'transition-[background-color,color,border-color,box-shadow,transform] duration-150',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
    'disabled:pointer-events-none disabled:opacity-60',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800',
        secondary:
          'border border-brand-600/25 bg-white/70 text-brand-600 hover:bg-white hover:border-brand-600/40',
        outline: 'border border-brand-600 text-brand-600 hover:bg-brand-50',
        ghost: 'text-content-muted hover:bg-brand-600/5 hover:text-brand-600',
        link: 'text-brand-600 underline-offset-4 hover:underline',
        danger: 'bg-danger text-white hover:opacity-90',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-4 text-sm',
        lg: 'h-[53px] px-6 text-base',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /** Affiche un spinner et désactive le bouton. */
    loading?: boolean;
    /** Icône rendue avant le libellé (masquée pendant le chargement). */
    icon?: ReactNode;
  };

export function Button({
  className,
  variant,
  size,
  fullWidth,
  loading = false,
  icon,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : icon}
      {children}
    </button>
  );
}

export { buttonVariants };
